import React, { useCallback } from 'react';
import {
  Box,
  Pagination as MuiPagination,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { PHONE_LANDSCAPE_MEDIA } from '../../theme-constants';

export interface PaginationProps {
  setPage: (newPage: number) => void;
  currentPage: number;
  pageCount: number;
  itemCount?: number;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  /**
   * Render the navigator even when there is only one page. Off by default: a
   * lone "1" is noise under most tables. A page with a fixed footer wants it
   * on, so the footer looks the same whether the list has 8 rows or 800.
   */
  showSinglePage?: boolean;
}

export function Pagination({
  setPage,
  currentPage,
  pageCount,
  size = 'small',
  className,
  showSinglePage = false,
}: PaginationProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleChange = useCallback(
    (_event: React.ChangeEvent<unknown>, page: number) => {
      setPage(page);
    },
    [setPage],
  );

  if (pageCount < (showSinglePage ? 1 : 2)) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        my: 2,
        // Every pixel of height counts on a phone in landscape.
        [PHONE_LANDSCAPE_MEDIA]: { my: 0.5 },
      }}
    >
      <MuiPagination
        count={pageCount}
        page={currentPage}
        onChange={handleChange}
        // Was hardcoded to 'small' while the prop defaulted to 'medium', so
        // `size` did nothing. Both callers that pass it pass 'small', and the
        // default is now 'small' too — so honouring it changes no rendering.
        size={size}
        className={className}
        // Desktop shows two siblings either side plus jump-to-first/last: up to
        // eleven controls. That wraps onto a second line on a phone, which is
        // how the "last page" button ended up orphaned below the row.
        //
        // On mobile: no first/last buttons, no siblings, but `boundaryCount: 1`
        // so page 1 and the final page stay directly reachable — which is what
        // the dropped jump buttons were for. Five controls at most.
        showFirstButton={!isMobile}
        showLastButton={!isMobile}
        siblingCount={isMobile ? 0 : 2}
        boundaryCount={isMobile ? 1 : 0}
        color="primary"
      />
    </Box>
  );
}
